import { LocaleDetails } from 'shared/src/types/languages.type';
import { getCurrencyForRegion } from 'shared/src/utils/currency';
import { formatBudget } from '@/lib/ui-content/budget-ui-content';
import { parsePriceRange } from 'shared/src/utils/price-range-estimate';

/**
 * The AI-text `product.priceRange` field is authored in English format
 * ("$950-1200 CAD") for every region. These helpers re-render it in the
 * viewer's locale at display time ("950 $ – 1 200 $" on fr-CA) so the whole
 * fleet is fixed without a data migration. The parser is deliberately
 * conservative: anything it cannot confidently read renders as the raw
 * stored string, exactly as before.
 *
 * Built on `formatBudget` (Intl.NumberFormat on locale.path) — NOT
 * `priceByCountry`, whose grouping is hardcoded to en-US.
 */

// The parser lives in shared (price-range-estimate.ts) since 2026-10-23: the
// rule that hides an estimate an observation contradicts reads the stored
// string with the very same parser the cards render it with, so a range the
// check cannot read is also one the card prints raw, as before.
export { parsePriceRange };

/**
 * The range a shop states on its page (`product.observedPriceRange`), in the
 * viewer's locale: "1 749 $ – 3 549 $". Cents are kept when the shop states
 * them (949,99 $), since these are the shop's own numbers, not an estimate.
 */
export function formatObservedPriceRange(
  range: { low: number; high: number } | null | undefined,
  locale: LocaleDetails,
): string {
  if (!range || !(range.low > 0) || !(range.high > range.low)) return '';
  const cents = !Number.isInteger(range.low) || !Number.isInteger(range.high);
  const format = (amount: number): string => {
    try {
      return new Intl.NumberFormat(locale.path, {
        style: 'currency',
        currency: getCurrencyForRegion(locale.region),
        minimumFractionDigits: cents ? 2 : 0,
        maximumFractionDigits: cents ? 2 : 0,
      }).format(amount);
    } catch {
      return formatBudget(amount, locale);
    }
  };
  return `${format(range.low)} – ${format(range.high)}`;
}

/** Locale-native "950 $ – 1 200 $" rendering; raw string on parse failure. */
export function formatPriceRangeForLocale(raw: string | null | undefined, locale: LocaleDetails): string {
  const parsed = parsePriceRange(raw);
  if (!parsed) return raw ?? '';
  try {
    return `${formatBudget(parsed.min, locale)} – ${formatBudget(parsed.max, locale)}`;
  } catch {
    return raw ?? '';
  }
}
